import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { FolderOutput } from '@core/api/generated'
import { type ManageFoldersState } from '@core/folders/manage-folders-dialog'

import { FoldersSelectionState } from './my-folders-selection'

export enum FilterType {
  all = 'all',
  clone = 'clone',
  internal = 'internal',
}

export type Filters = {
  folderQuery: string
  users: string[]
  type: FilterType
  domains: string[]
  topics: string[]
  profiles: string[]
}

export type Folder = FolderOutput

export type MyFoldersState = {
  filters: Filters

  manageFolders: ManageFoldersState

  selectedFolderIds: Set<string>

  // It's by folder! Example: { 'folderId1': new Set(['itemId1', 'itemId2']) }
  selectedItemsByFolder: Record<string, Set<string>>

  expandedFolderIds: Set<string>

  expandedItemSetIds: Set<string>

  // It's like a stored computed field - you should not modify it directly!
  folderSelectionState: FoldersSelectionState
}

const initialFilterState: Filters = {
  folderQuery: '',
  users: [],
  type: FilterType.all,
  domains: [],
  topics: [],
  profiles: [],
}

export const getInitialState = (): MyFoldersState => ({
  filters: initialFilterState,
  manageFolders: { mode: 'create', open: false, folder: null },
  selectedFolderIds: new Set(),
  expandedFolderIds: new Set(),
  expandedItemSetIds: new Set(),
  selectedItemsByFolder: {},

  folderSelectionState: {
    byFolderId: {},
    totalItemsSelected: 0,
    totalItemsWithDownloadPermission: 0,
    totalItemsWithWritePermission: 0,
    checkedFolderIds: [],
  },
})

type Actions = {
  reset: () => void
  toggleFolderSelected: (folderId: string, value: boolean) => void
  toggleOrSetFolderExpanded: (folderId: string, value?: boolean) => void
  toggleItemSetExpanded: (itemSetId: string) => void
  expandItemSets: (itemSetIds: string[]) => void
  expandFromBreadcrumbs: (
    breadcrumbs: { id: string }[],
    mode: 'override' | 'initial' | 'merge',
  ) => void
}

export const useMyFolders = create<MyFoldersState & Actions>()(
  immer((set) => {
    const actions: Actions = {
      reset: () => {
        set(getInitialState)
      },

      toggleFolderSelected: (folderId, value) => {
        set((state) => {
          if (value) {
            state.selectedFolderIds.add(folderId)
          } else {
            state.selectedFolderIds.delete(folderId)
          }
        })
      },
      toggleOrSetFolderExpanded: (folderId, value) => {
        set((state) => {
          const isExpanded = state.expandedFolderIds.has(folderId)
          const shouldExpand = value === undefined ? !isExpanded : value

          if (shouldExpand) {
            state.expandedFolderIds.add(folderId)
          } else {
            state.expandedFolderIds.delete(folderId)
          }
        })
      },
      toggleItemSetExpanded: (itemSetId) => {
        set((state) => {
          if (state.expandedItemSetIds.has(itemSetId)) {
            state.expandedItemSetIds.delete(itemSetId)
          } else {
            state.expandedItemSetIds.add(itemSetId)
          }
        })
      },
      expandItemSets: (itemSetIds) => {
        if (itemSetIds.length === 0) return
        set((state) => {
          for (const id of itemSetIds) {
            state.expandedItemSetIds.add(id)
          }
        })
      },
      expandFromBreadcrumbs: (breadcrumbs, mode = 'merge') => {
        set((state) => {
          if (mode === 'initial' && state.expandedFolderIds.size > 0) {
            return
          }

          const override = mode === 'override'

          if (override) {
            state.expandedFolderIds.clear()
          }

          breadcrumbs.forEach((breadcrumb) => {
            state.expandedFolderIds.add(breadcrumb.id)
          })
        })
      },
    }

    return { ...getInitialState(), ...actions }
  }),
)
